import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

/**
 * Renders the 2FA challenge step after a successful password/magic-link/OAuth.
 * Props:
 *  - twofa_token: short-lived token returned by the upstream auth call
 *  - email: optional, for display
 *  - onVerified: (userObject) => void  — called when verify-login returns the user
 *  - onCancel: () => void
 */
export default function TwoFactorChallenge({ twofa_token, email, onVerified, onCancel }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (code.length < 6) { toast.error("Enter your 6-digit code"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/verify-login", { twofa_token, code });
      onVerified?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="twofa-challenge">
      <div className="flex items-center gap-3">
        <ShieldCheck className="w-5 h-5 text-primary" />
        <div className="font-overline text-accent">Two-Factor Authentication</div>
      </div>
      <p className="text-sm text-muted-foreground">
        Enter the 6-digit code from your authenticator app
        {email ? <> for <b>{email}</b></> : null}.
      </p>
      <div>
        <Label className="font-overline">Authenticator code</Label>
        <Input className="rounded-none mt-2 font-mono-data tracking-widest text-lg" maxLength={6}
          value={code}
          autoFocus
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          placeholder="000000"
          data-testid="twofa-challenge-input" />
      </div>
      <div className="flex gap-2">
        <Button onClick={submit} disabled={busy || code.length < 6}
          className="flex-1 rounded-none uppercase tracking-wider h-11"
          data-testid="twofa-challenge-submit">
          {busy ? "…" : "Verify & Sign In"}
        </Button>
        {onCancel && (
          <Button onClick={onCancel} variant="outline" className="rounded-none uppercase tracking-wider h-11">
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}
